/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


import apiClient from './client';


export interface TollProviderDTO {
  id: string;
  name: string;
  category: string;
  loginUrl: string | null;
  stateCode: string | null;
  /** ISO 3166-1 alpha-2 (e.g. "US", "BR", "JP"). From plugin SupportedAgency.Country / DB. */
  countryCode: string | null;
  isTransponderIssuer: boolean;
  pluginName: string | null;
  /** True when the plugin uses an MFA email alias as its portal login.
   *  Declared by the plugin via `BaseTollProvider.RequiresMfaAlias` — no DB column,
   *  no hardcoded list on the host. A new plugin enables this by overriding the prop. */
  requiresMfaAlias: boolean;
  /** True when the plugin authenticates with a single API key instead of a portal
   *  login + password (e.g. Bestpass). Declared by the plugin via
   *  `BaseTollProvider.RequiresApiKey`; the credential is still stored in the same
   *  encrypted password field, only the form labels change. */
  requiresApiKey: boolean;
}

export interface TollPluginDTO {
  providerId: string;
  name: string;
  version: string;
  supportedRegions: string[];
  isAvailable: boolean;
}

export interface OwnerTollAccountDTO {
  id: string;
  tollProviderId: string;
  providerName: string;
  providerCategory: string;
  login: string;
  /**
   * Whether a credential is stored — the value itself is never returned. The server has no reason
   * to hand a portal password back to the browser, and the edit form used to depend on it doing so
   * (it pre-filled the field and posted the value straight back). Send a password only when it is
   * genuinely new; an empty one means "leave unchanged".
   */
  hasPassword: boolean;
  accountNumber: string | null;
  nickname: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Local-part of the MFA receive email ("joe-home"). Empty until set. */
  mfaAlias: string | null;
  /**
   * Full MFA email ("joe-home@mfa.myeztoll.com"). With no alias it is the owner's company mailbox
   * ("aegis@mail.myeztoll.com") when one exists — codes are forwarded there — else null.
   */
  mfaEmail: string | null;
  /** Domain used to compose mfaEmail ("mfa.myeztoll.com"). Same for all rows. */
  mfaAliasDomain: string | null;
  /** Tag numbers cached from the provider portal (OwnerProviderTransponders). Used for transponder dropdown on vehicle form. */
  cachedTransponders: string[];
}

export interface SaveTollAccountRequest {
  tollProviderId: string;
  login: string;
  password: string;
  accountNumber?: string;
  nickname?: string;
  isActive?: boolean;
  /**
   * Optional. If provided, must match `^[a-z0-9](?:[a-z0-9._-]{1,62}[a-z0-9])?$` and be globally unique.
   * On create: omit to let the server auto-generate a short random alias.
   * On update: omit to keep current; empty string to clear.
   */
  mfaAlias?: string;
}

export interface MfaAliasSuggestion {
  alias: string;
  domain: string;
  email: string;
}

export interface CarTransponderDTO {
  id: string;
  carId: string;
  tollProviderId: string;
  ownerTollAccountId: string | null;
  providerName: string;
  providerCategory: string;
  accountNickname: string | null;
  transponderNumber: string;
  isActive: boolean;
  transpondersLinkedToVehicles: boolean;
}

export interface SaveTransponderRequest {
  ownerTollAccountId: string;
  transponderNumber: string;
}

/** Per-car toll coverage summary — one row per car that has at least one agency link. */
export interface CarTollCoverageDTO {
  carId: string;
  /** Active CarTransponders rows for the car (= toll agencies it is linked to). */
  agencies: number;
  /** How many of those links still carry no transponder number. */
  agenciesWithoutTransponder: number;
}

export interface AvailableTransponderDTO {
  ownerTollAccountId: string;
  tagNumber: string;
  status: string | null;
  assignedPlateNumber: string | null;
}

export interface ScrapedTransactionDTO {
  providerName: string;
  /** Stable GUID of the OwnerTollAccount this transaction came from. Preferred over accountLogin for filtering. */
  ownerTollAccountId: string;
  accountLogin: string;
  externalTransactionId: string;
  carId: string;
  transponderNumber: string;
  plateNumber: string;
  plateState: string;
  amount: number;
  transactionDate: string;
  facilityName: string;
  entryLocation: string;
  exitLocation: string;
  agencyName: string;
  /** IANA timezone of the agency (e.g. "America/New_York"). Used for rendering transactionDate in clock time matching the agency portal. */
  agencyTimeZone?: string;
  isDuplicate: boolean;
}

export interface ProviderMaintenanceInfoDTO {
  providerName: string;
  message: string;
  detectedAt: string;
  retryAfter: string | null;
}

export interface AutoAssignLinkDTO {
  carId: string;
  carPlate: string;
  providerName: string;
  tollProviderId: string;
  transponderNumber: string;
}

export interface AutoAssignResultDTO {
  carsProcessed: number;
  accountsScanned: number;
  newLinks: number;
  alreadyLinked: number;
  errors: string[];
  links: AutoAssignLinkDTO[];
}

export interface TollScrapeResultDTO {
  ownerId: string;
  transactionsFound: number;
  newTransactions: number;
  duplicatesSkipped: number;
  errors: number;
  errorMessages: string[];
  transactions: ScrapedTransactionDTO[];
  duration: string;
  // MFA fields — populated when provider requires device verification
  mfaRequired: boolean;
  mfaAccountId: string | null;
  mfaProviderName: string | null;
  mfaMaskedEmail: string | null;
  mfaMaskedPhone: string | null;
  // Maintenance — populated when a provider is temporarily unavailable
  maintenanceProviders: ProviderMaintenanceInfoDTO[];
}

export interface MfaStatusDTO {
  mfaRequired: boolean;
  maskedEmail: string | null;
  maskedPhone: string | null;
  message: string | null;
  error: boolean;
}

export interface MfaActionResultDTO {
  success: boolean;
  message: string | null;
  deviceVerified: boolean;
  deviceId: string | null;
  /** After successful MFA, server immediately scrapes transactions using the verified session */
  scrapeResult: TollScrapeResultDTO | null;
}

export interface VehicleInfoDTO {
  vehicleId: string | null;
  plateNumber: string | null;
  plateState: string | null;
  make: string | null;
  model: string | null;
  year: string | null;
  color: string | null;
  transponderNumber: string | null;
  status: string | null;
  nickname: string | null;
}

export interface TransponderInfoDTO {
  transponderId: string | null;
  tagNumber: string | null;
  status: string | null;
  assignedVehicleId: string | null;
  assignedPlateNumber: string | null;
  type: string | null;
}

export interface VehicleListResultDTO {
  success: boolean;
  error: string | null;
  supportsVehicleList: boolean;
  transpondersLinkedToVehicles: boolean;
  vehicles: VehicleInfoDTO[];
  transponders: TransponderInfoDTO[];
  /** True when `transponders` is the last cached inventory, not a fresh read from the provider. */
  transpondersFromCache?: boolean;
  /** When that cache was last refreshed from the provider (UTC ISO). */
  transpondersCachedAt?: string | null;
  /** Why the live read produced nothing, when known. */
  transpondersError?: string | null;
}

/** Outcome of "Sync All Vehicles" (POST .../sync-all). */
export interface TollVehicleSyncResultDTO {
  success: boolean;
  error?: string;
  total: number;
  skippedInactive: number;
  skippedNoPlate: number;
  matched: number;
  created: number;
  linked: number;
  transponderUpdated: number;
  alreadyLinked: number;
  linkedToOtherAccount: number;
  tagConflicts: string[];
}

export interface TollVehicleScanDTO {
  accountId: string;
  status: 'processing' | 'completed' | 'error' | 'not_found';
  progress: number;
  step?: string;
  startedAt?: string;
  completedAt?: string;
  error?: string | null;
  /** Full vehicle + transponder payload — present only when status==='completed'. */
  result?: VehicleListResultDTO | null;
}

export interface TollTransactionScanDTO {
  ownerId: string;
  status: 'processing' | 'completed' | 'error' | 'not_found';
  progress: number;
  step?: string;
  startedAt?: string;
  completedAt?: string;
  error?: string | null;
  /** Full scrape result (transactions) — present only when status==='completed'. */
  result?: TollScrapeResultDTO | null;
}

export interface PlateCheckResult {
  status: string;
  carId?: string;
  dbTransponder?: string;
  dbMake?: string;
  dbModel?: string;
  dbYear?: string;
  dbColor?: string;
  /** Plate state on the DB record — empty when the car came from a platform that supplies none. */
  dbPlateState?: string;
}

export interface SaveScrapedTransactionsResponse {
  savedCount: number;
  skippedDuplicates: number;
  skippedNoCarMatch: number;
  errorCount: number;
  errors: string[];
}

export const tollProviderApi = {
  /** GET /api/TollProvider/providers — all active toll providers */
  getProviders: async (): Promise<TollProviderDTO[]> => {
    const res = await apiClient.get<{ result: TollProviderDTO[] }>('/TollProvider/providers');
    return res.data.result || [];
  },

  /** GET /api/TollProvider/plugins — available scraping plugins (DLLs in Plugins/ folder) */
  getPlugins: async (): Promise<TollPluginDTO[]> => {
    const res = await apiClient.get<{ result: TollPluginDTO[] }>('/TollProvider/plugins');
    return res.data.result || [];
  },

  /** POST /api/TollProvider/test-connection/{ownerId}/{accountId} — test credentials */
  testConnection: async (ownerId: string, accountId: string): Promise<boolean> => {
    const res = await apiClient.post<{ result: { success: boolean } }>(`/TollProvider/test-connection/${ownerId}/${accountId}`);
    return res.data.result.success;
  },

  /** GET /api/TollProvider/accounts/{ownerId} — owner's toll accounts */
  getOwnerAccounts: async (ownerId: string): Promise<OwnerTollAccountDTO[]> => {
    const res = await apiClient.get<{ result: OwnerTollAccountDTO[] }>(`/TollProvider/accounts/${ownerId}`);
    return res.data.result || [];
  },

  /** POST /api/TollProvider/accounts/{ownerId} — create new account */
  createAccount: async (ownerId: string, data: SaveTollAccountRequest): Promise<string> => {
    const res = await apiClient.post<{ result: { id: string } }>(`/TollProvider/accounts/${ownerId}`, data);
    return res.data.result.id;
  },

  /** PUT /api/TollProvider/accounts/{ownerId}/{accountId} — update account */
  updateAccount: async (ownerId: string, accountId: string, data: SaveTollAccountRequest): Promise<boolean> => {
    const res = await apiClient.put<{ result: boolean }>(`/TollProvider/accounts/${ownerId}/${accountId}`, data);
    return res.data.result;
  },

  /** DELETE /api/TollProvider/accounts/{ownerId}/{accountId} — delete account */
  deleteAccount: async (ownerId: string, accountId: string): Promise<boolean> => {
    const res = await apiClient.delete<{ result: boolean }>(`/TollProvider/accounts/${ownerId}/${accountId}`);
    return res.data.result;
  },

  /** GET /api/TollProvider/accounts/mfa-alias/suggest — fresh unique alias (not persisted) */
  suggestMfaAlias: async (): Promise<MfaAliasSuggestion> => {
    const res = await apiClient.get<{ result: MfaAliasSuggestion }>(`/TollProvider/accounts/mfa-alias/suggest`);
    return res.data.result;
  },

  // ── Car Transponders ──

  /** GET /api/TollProvider/transponders/{carId} */
  getCarTransponders: async (carId: string): Promise<CarTransponderDTO[]> => {
    const res = await apiClient.get<{ result: CarTransponderDTO[] }>(`/TollProvider/transponders/${carId}`);
    return res.data.result || [];
  },

  /** GET /api/TollProvider/car-toll-coverage/{ownerId}
   *  Whole-fleet toll coverage in ONE call — used to colour the vehicle list rows.
   *  Cars absent from the response have no agency link at all. */
  getCarTollCoverage: async (ownerId: string): Promise<CarTollCoverageDTO[]> => {
    const res = await apiClient.get<{ result: CarTollCoverageDTO[] }>(
      `/TollProvider/car-toll-coverage/${encodeURIComponent(ownerId)}`);
    return res.data.result || [];
  },

  /** GET /api/TollProvider/available-transponders/{ownerId}?carId={carId}
   *  Transponders cached from the owner's provider accounts that are NOT assigned to another car.
   *  Used to populate the transponder dropdown on the vehicle form. */
  getAvailableTransponders: async (ownerId: string, carId?: string): Promise<AvailableTransponderDTO[]> => {
    const query = carId ? `?carId=${encodeURIComponent(carId)}` : '';
    const res = await apiClient.get<{ result: AvailableTransponderDTO[] }>(`/TollProvider/available-transponders/${ownerId}${query}`);
    return res.data.result || [];
  },

  /** POST /api/TollProvider/transponders/{carId} */
  addTransponder: async (carId: string, data: SaveTransponderRequest): Promise<{ id: string; warning?: string }> => {
    const res = await apiClient.post<{ result: { id: string }; warning?: string }>(`/TollProvider/transponders/${carId}`, data);
    return { id: res.data.result.id, warning: res.data.warning };
  },

  /** DELETE /api/TollProvider/transponders/{carId}/{transponderId} */
  deleteTransponder: async (carId: string, transponderId: string): Promise<boolean> => {
    const res = await apiClient.delete<{ result: boolean }>(`/TollProvider/transponders/${carId}/${transponderId}`);
    return res.data.result;
  },

  // ── Toll Scraping ──

  /** POST /api/TollProvider/scrape-car/{carId} — scrape tolls for a specific car */
  scrapeForCar: async (carId: string, fromDate?: string, toDate?: string): Promise<TollScrapeResultDTO> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await apiClient.post<{ result: TollScrapeResultDTO }>(`/TollProvider/scrape-car/${carId}${query}`);
    return res.data.result;
  },

  /** GET /api/TollProvider/raw-transactions/{ownerId} — read persisted raw scrape feed.
   *  Used by Toll Transactions page on initial load (no live scrape). */
  getRawTransactions: async (ownerId: string, fromDate?: string, toDate?: string, accountIds?: string[]): Promise<ScrapedTransactionDTO[]> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    if (accountIds && accountIds.length > 0) {
      for (const id of accountIds) params.append('accountIds', id);
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await apiClient.get<{ transactions: ScrapedTransactionDTO[]; count: number }>(
      `/TollProvider/raw-transactions/${ownerId}${query}`
    );
    return res.data.transactions ?? [];
  },

  /** POST /api/TollProvider/scrape/{ownerId} — scrape tolls for owner accounts.
   *  Optional `accountIds` filters to a subset; omitted/empty scrapes all active accounts. */
  scrapeForOwner: async (ownerId: string, fromDate?: string, toDate?: string, accountIds?: string[]): Promise<TollScrapeResultDTO> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    if (accountIds && accountIds.length > 0) {
      for (const id of accountIds) params.append('accountIds', id);
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await apiClient.post<{ result: TollScrapeResultDTO }>(`/TollProvider/scrape/${ownerId}${query}`);
    return res.data.result;
  },

  /** POST — kick off a BACKGROUND transaction scrape (progress bar). Returns immediately. */
  startTransactionScan: async (ownerId: string, fromDate?: string, toDate?: string, accountIds?: string[]): Promise<TollTransactionScanDTO> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    if (accountIds && accountIds.length > 0) {
      for (const id of accountIds) params.append('accountIds', id);
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await apiClient.post<TollTransactionScanDTO>(`/TollProvider/scrape/${ownerId}/scan${query}`);
    return res.data;
  },

  /** GET — poll the background transaction scrape; includes the result when status==='completed'. */
  getTransactionScan: async (ownerId: string): Promise<TollTransactionScanDTO> => {
    const res = await apiClient.get<TollTransactionScanDTO>(`/TollProvider/scrape/${ownerId}/scan`);
    return res.data;
  },

  /** POST /api/TollProvider/assign-providers/{ownerId}
   *  Walk owner's cars + active toll accounts, match by plate, create missing CarTransponders links.
   *  Idempotent — already-linked cars are skipped. */
  autoAssignProviders: async (ownerId: string): Promise<AutoAssignResultDTO> => {
    const res = await apiClient.post<{ result: AutoAssignResultDTO }>(`/TollProvider/assign-providers/${ownerId}`);
    return res.data.result;
  },

  // ── MFA (Device Verification) ──

  /** POST /api/TollProvider/mfa/check/{ownerId}/{accountId} — check MFA status */
  checkMfa: async (ownerId: string, accountId: string): Promise<MfaStatusDTO> => {
    const res = await apiClient.post<{ result: MfaStatusDTO }>(`/TollProvider/mfa/check/${ownerId}/${accountId}`);
    return res.data.result;
  },

  /** POST /api/TollProvider/mfa/request-code/{ownerId}/{accountId} — send verification code */
  requestMfaCode: async (ownerId: string, accountId: string, method: string): Promise<MfaActionResultDTO> => {
    const res = await apiClient.post<{ result: MfaActionResultDTO }>(
      `/TollProvider/mfa/request-code/${ownerId}/${accountId}`,
      { method }
    );
    return res.data.result;
  },

  /** POST /api/TollProvider/mfa/verify/{ownerId}/{accountId} — submit verification code */
  verifyMfaCode: async (ownerId: string, accountId: string, code: string, method: string): Promise<MfaActionResultDTO> => {
    const res = await apiClient.post<{ result: MfaActionResultDTO }>(
      `/TollProvider/mfa/verify/${ownerId}/${accountId}`,
      { code, method }
    );
    return res.data.result;
  },

  /** GET /api/TollProvider/vehicles/{ownerId}/{accountId} — get vehicles from toll provider */
  getVehicles: async (ownerId: string, accountId: string): Promise<VehicleListResultDTO> => {
    const res = await apiClient.get<{ result: VehicleListResultDTO }>(`/TollProvider/vehicles/${ownerId}/${accountId}`);
    return res.data.result;
  },

  /** POST — kick off a background full load of vehicles + complete transponder inventory (progress bar). */
  startVehicleScan: async (ownerId: string, accountId: string): Promise<TollVehicleScanDTO> => {
    const res = await apiClient.post<TollVehicleScanDTO>(`/TollProvider/vehicles/${ownerId}/${accountId}/scan`);
    return res.data;
  },

  /** GET — poll the background scan; includes the full result when status==='completed'. */
  getVehicleScan: async (ownerId: string, accountId: string): Promise<TollVehicleScanDTO> => {
    const res = await apiClient.get<TollVehicleScanDTO>(`/TollProvider/vehicles/${ownerId}/${accountId}/scan`);
    return res.data;
  },

  /** POST — "Sync All Vehicles": match every active agency vehicle to the fleet, create missing cars, link them to this account. */
  syncAllVehicles: async (ownerId: string, accountId: string): Promise<TollVehicleSyncResultDTO> => {
    const res = await apiClient.post<{ result: TollVehicleSyncResultDTO }>(`/TollProvider/vehicles/${ownerId}/${accountId}/sync-all`);
    return res.data.result;
  },

  /** POST /api/TollPayment/save-scraped-transactions — save scraped transactions to billing system */
  saveToSystem: async (transactions: ScrapedTransactionDTO[]): Promise<SaveScrapedTransactionsResponse> => {
    const res = await apiClient.post<{ result: SaveScrapedTransactionsResponse }>(
      '/TollPayment/save-scraped-transactions',
      transactions
    );
    return res.data.result;
  },

  /** POST /api/TollProvider/check-plates — check which plates exist in DB
   * Returns "plate|state" → { status: "not_found"|"found_not_linked"|"linked", dbTransponder?: string }
   * Pass tollProviderId to also check if car has a transponder for that provider */
  checkPlatesExist: async (
    plates: { plateNumber: string; plateState: string }[],
    tollProviderId?: string,
    ownerId?: string
  ): Promise<{ plates: Record<string, PlateCheckResult>; assignedTransponders: string[] }> => {
    const params = new URLSearchParams();
    if (tollProviderId) params.set('tollProviderId', tollProviderId);
    if (ownerId) params.set('ownerId', ownerId);
    const query = params.toString() ? `?${params}` : '';
    const res = await apiClient.post<{ result: Record<string, PlateCheckResult>; assignedTransponders?: string[] }>(
      `/TollProvider/check-plates${query}`, plates
    );
    return { plates: res.data.result || {}, assignedTransponders: res.data.assignedTransponders || [] };
  },
};
